import { useState, useRef, useEffect } from 'react';

interface SpeedDialAction {
  label: string;
  icon: string;
  onClick: () => void;
}

interface SpeedDialFABProps {
  actions?: SpeedDialAction[];
  position?: 'bottom-right' | 'bottom-left';
}

/**
 * SpeedDialFAB - Expandable floating action button
 * Shows a "+" that expands to reveal multiple actions
 */
function SpeedDialFAB({ actions = [], position = 'bottom-right' }: SpeedDialFABProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const handleActionClick = (action: SpeedDialAction) => {
    setOpen(false);
    action.onClick();
  };

  const positionClass = position === 'bottom-left' ? 'speed-dial--left' : '';

  return (
    <div
      ref={containerRef}
      className={`speed-dial ${positionClass} ${open ? 'speed-dial--open' : ''}`}
    >
      {/* Backdrop overlay when open */}
      {open && <div className="speed-dial-backdrop" onClick={() => setOpen(false)} />}

      {/* Action buttons - appear above main FAB */}
      <div className="speed-dial-actions">
        {actions.map((action, index) => (
          <button
            key={index}
            className="speed-dial-action"
            onClick={() => handleActionClick(action)}
            style={{ transitionDelay: open ? `${index * 50}ms` : '0ms' }}
          >
            <span className="speed-dial-action-icon">{action.icon}</span>
            <span className="speed-dial-action-label">{action.label}</span>
          </button>
        ))}
      </div>

      {/* Main FAB button */}
      <button
        className="speed-dial-trigger"
        onClick={() => setOpen(!open)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        <span className="speed-dial-trigger-icon">+</span>
      </button>
    </div>
  );
}

export default SpeedDialFAB;
